/**
 * 각 게임별 이미지 URL들을 정의하는 유틸리티
 * 게임 이미지 미리 로딩(preload)을 위해 사용됩니다.
 */

import globalImageCache from './globalImageCache';

// 피드백 이미지들 (public 폴더 경로)
const bubbleSuccessUrl = '/assets/images/contents/shortForm/bubble/Bubble_Success.png';
const bubbleFailUrl = '/assets/images/contents/shortForm/bubble/Bubble_Fail.png';
// 뽁뽁이 게임 이미지들 (public 폴더 경로)
const bubbleLongUrl = '/assets/images/contents/shortForm/bubble/Bubble_long.png';
const bubbleLongPoppedUrl = '/assets/images/contents/shortForm/bubble/Bubble_Long_Poped.png';
// 지우고 뽑기 게임 이미지들 (public 폴더 경로)
const coverImageWebp = '/assets/images/contents/shortForm/erase/Cover.webp';
const gem1 = '/assets/images/contents/shortForm/erase/Gem1.png';
const gem2 = '/assets/images/contents/shortForm/erase/Gem2.png';
const junk2 = '/assets/images/contents/shortForm/erase/junk2.png';
const junk3 = '/assets/images/contents/shortForm/erase/junk3.png';
const stone = '/assets/images/contents/shortForm/erase/Stone.png';

// 풍선 터트리기 게임 이미지들 (public 폴더)
const skyBalloonUrl = '/assets/images/contents/shortForm/SkyblueBalloon.png';
const yellowBalloonUrl = '/assets/images/contents/shortForm/YellowBalloon.png';
const purpleBalloonUrl = '/assets/images/contents/shortForm/PuppleBalloon.png';
const pinkBalloonUrl = '/assets/images/contents/shortForm/PinkBalloon.png';
const slingshotUrl = '/assets/images/contents/shortForm/Slingshot.png';
const crosshairUrl = '/assets/images/contents/shortForm/Crosshair.png';

// 로봇 팩토리 게임 이미지들 (public 폴더)
const robotArrowUrl = '/assets/images/contents/shortForm/RobotArrow.png';
const robotBodyEmptyUrl = '/assets/images/contents/shortForm/RobotBodyEmpty.png';
const robotHeadEmptyUrl = '/assets/images/contents/shortForm/RobotHeadEmpty.png';
const robotLegEmptyUrl = '/assets/images/contents/shortForm/RobotLegEmpty.png';
const robotBodyUrl = '/assets/images/contents/shortForm/RobotBody.png';
const robotHeadUrl = '/assets/images/contents/shortForm/RobotHead.png';
const robotLegUrl = '/assets/images/contents/shortForm/RobotLeg.png';
const robotBodyQuizUrl = '/assets/images/contents/shortForm/RobotBodyQuiz.png';

/**
 * 게임 타입에 따른 이미지 URL 목록을 반환
 * @param {string} gameType - 게임 타입 ('bubbleWrap', 'balloonPop', 'robotFactory', 'eraseAndPick')
 * @param {string} bubbleImage - 뽁뽁이 게임의 기본 풍선 이미지 URL
 * @param {string} bubbleFilledImage - 뽁뽁이 게임의 터진 풍선 이미지 URL
 * @returns {string[]} 이미지 URL 배열
 */
export const getGameImageUrls = (gameType, bubbleImage = '', bubbleFilledImage = '') => {
  const commonImages = [bubbleSuccessUrl, bubbleFailUrl];
  
  switch (gameType) {
    case 'bubbleWrap':
      return [
        ...commonImages,
        bubbleImage,
        bubbleFilledImage,
        bubbleLongUrl,
        bubbleLongPoppedUrl
      ].filter(Boolean); // 빈 문자열 제거
      
    case 'balloonPop':
      return [
        ...commonImages,
        skyBalloonUrl,
        yellowBalloonUrl,
        purpleBalloonUrl,
        pinkBalloonUrl,
        slingshotUrl,
        crosshairUrl
      ];
      
    case 'robotFactory':
      return [
        ...commonImages,
        robotArrowUrl,
        robotBodyEmptyUrl,
        robotHeadEmptyUrl,
        robotLegEmptyUrl,
        robotBodyUrl,
        robotHeadUrl,
        robotLegUrl,
        robotBodyQuizUrl
      ];
      
    case 'eraseAndPick':
      return [
        ...commonImages,
        coverImageWebp,
        gem1,
        gem2,
        junk2,
        junk3,
        stone
      ];
      
    default:
      return commonImages;
  }
};

/**
 * 스테이지 번호에 따른 게임 타입을 반환
 * @param {number} stage - 스테이지 번호 (1부터 시작)
 * @param {boolean} includeTutorial - 튜토리얼 포함 여부
 * @returns {string} 게임 타입
 */
export const getGameTypeByStage = (stage, includeTutorial = false) => {
  if (includeTutorial) {
    // 튜토리얼 포함 모드
    if (stage === 1) return 'bubbleWrap';     // 튜토리얼 1: 뽁뽁이
    if (stage === 2) return 'balloonPop';    // 튜토리얼 2: 풍선터트리기
    if (stage === 3) return 'robotFactory';  // 튜토리얼 3: 로봇팩토리
    if (stage === 4) return 'eraseAndPick';  // 튜토리얼 4: 지우고뽑기
    
    // 퀴즈 단계 (stage 5부터)
    const quizIndex = stage - 5; // 5번째 스테이지부터는 quizIndex 0
    const gameTypes = ['bubbleWrap', 'balloonPop', 'robotFactory', 'eraseAndPick'];
    return gameTypes[quizIndex % 4];
  } else {
    // 튜토리얼 없음 모드 (stage 1부터 바로 퀴즈)
    const gameTypes = ['bubbleWrap', 'balloonPop', 'robotFactory', 'eraseAndPick'];
    return gameTypes[(stage - 1) % 4];
  }
};

/**
 * 이미지 배열을 미리 로딩하고 전역 캐시에 저장
 * @param {string[]} imageUrls - 로딩할 이미지 URL 배열
 * @param {function} onProgress - 진행률 콜백 (progress, loaded, total) => void
 * @param {function} onComplete - 완료 콜백 () => void
 * @param {function} onError - 에러 콜백 (error, url) => void
 * @returns {function} 로딩을 중단하는 함수
 */
export const preloadGameImages = (imageUrls, onProgress, onComplete, onError) => {
  if (!imageUrls || imageUrls.length === 0) {
    if (onComplete) onComplete();
    return () => {};
  }

  let loaded = 0;
  let cancelled = false;
  const total = imageUrls.length;
  const images = [];

  const checkComplete = () => {
    if (cancelled) return;
    
    const progress = total > 0 ? loaded / total : 1;
    if (onProgress) onProgress(progress, loaded, total);
    
    if (loaded >= total) {
      globalImageCache.setReady(true);
      if (onComplete) onComplete();
    }
  };

  const loadImage = (url) => {
    if (cancelled) return;
    
    // 이미 캐시에 있고 사용 가능하면 스킵
    if (globalImageCache.isAvailable(url)) {
      loaded++;
      checkComplete();
      return;
    }
    
    const img = new Image();
    images.push(img);
    
    img.onload = () => {
      if (cancelled) return;
      
      // 전역 캐시에 저장
      globalImageCache.set(url, img);
      loaded++;
      checkComplete();
    };
    
    img.onerror = (error) => {
      if (cancelled) return;
      loaded++; // 에러도 완료로 카운트
      if (onError) onError(error, url);
      checkComplete();
    };
    
    img.src = url;
  };

  // 모든 이미지 로딩 시작
  imageUrls.forEach(loadImage);

  // 로딩 중단 함수 반환
  return () => {
    cancelled = true;
    images.forEach(img => {
      img.onload = null;
      img.onerror = null;
    });
  };
};

export default {
  getGameImageUrls,
  getGameTypeByStage,
  preloadGameImages
};
